/**
 * GID-218: a dropdown whose list lives in our document.
 *
 * The Route picker's options were rendering over the Gantt grid, hundreds of
 * pixels from the control, on a dual-monitor setup. A native <select> popup is
 * drawn by the WebView host using SCREEN coordinates, and inside an Office task
 * pane -- nested iframes, per-monitor DPI -- that origin comes out wrong. No
 * page CSS can move it, because the popup is not part of our document.
 *
 * Moving the list into the DOM makes position our problem, which we control.
 * The pane already did this for the model-tier picker; this generalises that
 * pattern so any <select> can be upgraded.
 *
 * The native <select> deliberately stays in the DOM as the value holder. Every
 * existing reader keeps working untouched:
 *
 *     takeRouteOverride(routeOverrideSelect)   // reads and resets .value
 *     editHarnessModeSelect?.value             // reads .value
 *
 * so this change is invisible above the widget.
 */

import { fitMenuToViewport } from "./menuPlacement.js";

const OPEN_KEYS = new Set(["ArrowDown", "ArrowUp", "Enter", " "]);

function optionsOf(selectEl) {
  return Array.from(selectEl.options || []).map((o) => ({
    value: o.value,
    text: o.textContent || o.value,
    title: o.title || "",
  }));
}

/**
 * Upgrade a native <select> into an in-document listbox.
 *
 * @param {HTMLSelectElement|null} selectEl
 * @returns {{open: Function, close: Function, sync: Function, destroy: Function}}
 */
export function enhanceSelect(selectEl) {
  const noop = { open() {}, close() {}, sync() {}, destroy() {} };
  if (!selectEl || !selectEl.options) return noop;
  if (selectEl.dataset.listboxEnhanced === "true") return noop;

  const doc = selectEl.ownerDocument;
  const wrap = doc.createElement("div");
  wrap.className = "listbox-select";

  const toggle = doc.createElement("button");
  toggle.type = "button";
  toggle.className = "listbox-select-toggle";
  toggle.setAttribute("aria-haspopup", "listbox");
  toggle.setAttribute("aria-expanded", "false");
  if (selectEl.title) toggle.title = selectEl.title;

  const labelSpan = doc.createElement("span");
  labelSpan.className = "listbox-select-label";
  const caret = doc.createElement("span");
  caret.className = "listbox-select-caret";
  caret.setAttribute("aria-hidden", "true");
  caret.textContent = "▾";
  toggle.append(labelSpan, caret);

  const menu = doc.createElement("ul");
  menu.className = "listbox-select-menu";
  menu.setAttribute("role", "listbox");
  menu.hidden = true;

  // The label the select carried should point at the new control, not at a
  // select that is now hidden from assistive tech.
  const labelEl = selectEl.id ? doc.querySelector(`label[for="${selectEl.id}"]`) : null;
  if (labelEl) {
    toggle.id = `${selectEl.id}_toggle`;
    labelEl.setAttribute("for", toggle.id);
    menu.setAttribute("aria-labelledby", toggle.id);
  }

  selectEl.parentNode.insertBefore(wrap, selectEl);
  wrap.append(toggle, menu);
  wrap.appendChild(selectEl);

  // Kept as the value holder, taken out of the a11y tree and the tab order so
  // there is exactly one control, not two.
  selectEl.dataset.listboxEnhanced = "true";
  selectEl.setAttribute("aria-hidden", "true");
  selectEl.tabIndex = -1;
  selectEl.classList.add("listbox-select-native");

  let activeIndex = 0;

  function render() {
    const opts = optionsOf(selectEl);
    const current = selectEl.value;
    menu.innerHTML = "";
    opts.forEach((opt, i) => {
      const li = doc.createElement("li");
      li.className = "listbox-select-option";
      li.setAttribute("role", "option");
      li.dataset.value = opt.value;
      li.textContent = opt.text;
      if (opt.title) li.title = opt.title;
      const isActive = opt.value === current;
      li.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive) {
        li.classList.add("is-active");
        activeIndex = i;
      }
      li.addEventListener("click", () => choose(opt.value));
      menu.appendChild(li);
    });
    const selectedOpt = opts.find((o) => o.value === current) || opts[0];
    labelSpan.textContent = selectedOpt ? selectedOpt.text : "";
  }

  function choose(value) {
    selectEl.value = value;
    // Dispatched on the native element so existing change listeners, and any
    // future ones, do not need to know this widget exists.
    selectEl.dispatchEvent(new Event("change", { bubbles: true }));
    render();
    close();
  }

  function open() {
    if (selectEl.disabled) return;
    render();
    menu.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    fitMenuToViewport(toggle, menu);
    highlight(activeIndex);
  }

  function close() {
    menu.hidden = true;
    toggle.setAttribute("aria-expanded", "false");
  }

  function highlight(i) {
    const items = Array.from(menu.children);
    if (!items.length) return;
    activeIndex = Math.max(0, Math.min(i, items.length - 1));
    items.forEach((el, idx) => el.classList.toggle("is-highlighted", idx === activeIndex));
  }

  // Deliberately does NOT stop propagation: the composer holds three of these
  // side by side, and each closes the others through its own document listener.
  // Swallowing the click here would leave two menus open on top of each other.
  // The listener below is guarded by wrap.contains, so this one cannot close
  // the menu it just opened.
  function onToggleClick() {
    if (menu.hidden) open();
    else close();
  }

  function onDocumentClick(e) {
    if (!wrap.contains(e.target)) close();
  }

  function onKeyDown(e) {
    if (e.key === "Escape") {
      if (!menu.hidden) {
        close();
        toggle.focus();
      }
      return;
    }
    if (!wrap.contains(e.target)) return;

    if (menu.hidden) {
      if (OPEN_KEYS.has(e.key)) {
        e.preventDefault();
        open();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      highlight(activeIndex + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      highlight(activeIndex - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const el = menu.children[activeIndex];
      if (el) choose(el.dataset.value);
    }
  }

  toggle.addEventListener("click", onToggleClick);
  doc.addEventListener("click", onDocumentClick);
  // One keydown listener, at the document, not one per element: keydown bubbles,
  // so a listener on the toggle AND one here would handle the same press twice
  // and move the highlight two rows per ArrowDown.
  doc.addEventListener("keydown", onKeyDown);
  // Anything that sets .value programmatically can call sync(); a change event
  // on the select (from other code) keeps the label honest too.
  selectEl.addEventListener("change", render);

  render();

  return {
    open,
    close,
    sync: render,
    destroy() {
      doc.removeEventListener("click", onDocumentClick);
      doc.removeEventListener("keydown", onKeyDown);
      selectEl.removeEventListener("change", render);
      delete selectEl.dataset.listboxEnhanced;
      selectEl.removeAttribute("aria-hidden");
      selectEl.classList.remove("listbox-select-native");
      selectEl.tabIndex = 0;
      wrap.parentNode.insertBefore(selectEl, wrap);
      wrap.remove();
    },
  };
}
